import { useMemo } from 'react';
import { scaleLinear, quantile, median } from 'd3';
import { Chart } from './Chart';

function buildRows(laps) {
  const byDriver = new Map();
  for (const l of laps) {
    const t = Number(l.lap_time_seconds);
    if (!isFinite(t) || t <= 0) continue;
    if (!byDriver.has(l.driver)) byDriver.set(l.driver, []);
    byDriver.get(l.driver).push({
      t,
      lap: Number(l.lap_number),
      isPit: !!(l.is_pit_in || l.is_pit_out),
    });
  }

  const rows = [];
  for (const [driver, all] of byDriver.entries()) {
    if (all.length === 0) continue;
    all.sort((a, b) => a.lap - b.lap);
    const rawMed = median(all.map((p) => p.t));

    // strip pit laps + lap 1 of session
    let pool = all.filter((p) => !p.isPit && p.lap > 1).map((p) => p.t);
    if (pool.length < 4) continue;

    pool.sort((a, b) => a - b);
    const q1 = quantile(pool, 0.25);
    const q3 = quantile(pool, 0.75);
    const iqr = q3 - q1;
    // slow-side only — the pit/yellow/traffic noise sits above the median
    const cutHi = q3 + 1.5 * iqr;
    const filtered = pool.filter((t) => t <= cutHi);
    if (filtered.length < 4) continue;

    rows.push({
      driver,
      med: median(filtered),
      p25: quantile(filtered, 0.25),
      p75: quantile(filtered, 0.75),
      rawMed,
      n: filtered.length,
    });
  }

  rows.sort((a, b) => a.med - b.med);
  return rows;
}

export const TruePace = ({ laps }) => {
  const rows = useMemo(() => buildRows(laps || []), [laps]);

  if (rows.length === 0) {
    return (
      <div className="absolute inset-0 grid place-items-center text-xs text-f1-muted px-6 text-center">
        Not enough green laps for a clean-air estimate.
      </div>
    );
  }

  const leader = rows[0].med;

  return (
    <Chart>
      {({ width, height }) => {
        const margin = { top: 4, right: 64, bottom: 4, left: 36 };
        const innerW = Math.max(0, width - margin.left - margin.right);
        const innerH = Math.max(0, height - margin.top - margin.bottom);
        const visible = rows.slice(0, 14);
        const rowH = innerH / Math.max(visible.length, 1);
        const barH = Math.max(3, Math.min(10, rowH - 2));

        const lo = Math.min(leader, ...visible.map((r) => r.p25));
        const hi = Math.max(...visible.map((r) => Math.max(r.p75, r.rawMed)));
        const x = scaleLinear().domain([lo, hi]).range([0, innerW]).nice();

        return (
          <svg width={width} height={height}>
            <g transform={`translate(${margin.left},${margin.top})`}>
              {visible.map((r, i) => {
                const y = i * rowH + (rowH - barH) / 2;
                const x25 = x(r.p25);
                const x75 = x(r.p75);
                const xMed = x(r.med);
                const xRaw = x(r.rawMed);
                const delta = r.med - leader;
                return (
                  <g key={r.driver}>
                    <text x={-6} y={y + barH * 0.85} textAnchor="end" fontSize="9" fill="#9ca3af">
                      {r.driver}
                    </text>
                    {/* raw-median ghost — the gap to the candle = traffic effect */}
                    <line
                      x1={xRaw}
                      x2={xRaw}
                      y1={y - 1}
                      y2={y + barH + 1}
                      stroke="#6b7280"
                      strokeOpacity={0.5}
                      strokeWidth={1}
                    />
                    <circle cx={xRaw} cy={y + barH / 2} r={1.5} fill="#6b7280" opacity={0.6} />
                    {/* candle */}
                    <rect
                      x={x25}
                      y={y}
                      width={Math.max(1, x75 - x25)}
                      height={barH}
                      fill="#e10600"
                      opacity={0.35}
                    >
                      <title>
                        {r.driver}
                        {`\nclean-air median: ${r.med.toFixed(3)}s`}
                        {`\nP25 / P75: ${r.p25.toFixed(3)}s / ${r.p75.toFixed(3)}s`}
                        {`\nraw median: ${r.rawMed.toFixed(3)}s (Δ ${(r.rawMed - r.med >= 0 ? '+' : '')}${(r.rawMed - r.med).toFixed(3)}s)`}
                        {`\nlaps after filter: ${r.n}`}
                      </title>
                    </rect>
                    <line
                      x1={xMed}
                      x2={xMed}
                      y1={y - 1}
                      y2={y + barH + 1}
                      stroke="#e10600"
                      strokeWidth={1.5}
                    />
                    <text
                      x={Math.max(xMed, x75) + 4}
                      y={y + barH * 0.85}
                      fontSize="9"
                      fill="#e5e5e5"
                    >
                      {i === 0 ? `${r.med.toFixed(2)}s` : `+${delta.toFixed(2)}`}
                    </text>
                  </g>
                );
              })}
            </g>
          </svg>
        );
      }}
    </Chart>
  );
};
